import invariant from 'tiny-invariant';
import { groupBy } from 'lodash';
import moment from 'moment';
import {
  ConversationMessagesHQ_conversationMessages_messages as ConvoMessage,
  ConversationMessagesHQ_conversationMessages_messages_message_composer as ConvoMessageComposer,
} from '../gql/__generated__/ConversationMessagesHQ';

// TODO what is the messages direction?
// do we care about that at this stage? (we might not given how the code is structured)

/**
 * We group messages withing a time window of 5 minutes
 */
const MESSAGE_BATCH_TIME_WINDOW = 1000 * 60 * 5; // 5 min

/**
 *  Represents a date separator in the list of messages
 */
export interface IDateSeparator {
  type: 'DateSeparator';
  date: string;
}

/**
 *  Represents a group of messages by the same author
 *  and in a close span of time that we render together
 */
export interface IMessageGroup {
  type: 'MessageGroup';
  date: string;
  messages: Array<ConvoMessage>;
  composer: ConvoMessageComposer;
}

/**
 * Grab a list of messages and create a list of chunks  for the UI to render.
 * Based on src/chat/components/Stream/chunker.ts
 */
export function createMessageGroupsAndDates(
  messages: Array<ConvoMessage>
): Array<IMessageGroup | IDateSeparator> {
  return insertDateSeparators(createMessageGroups(messages));
}

export default createMessageGroupsAndDates;

/**
 * Grab a list of messages and create a list of groups for the UI to render.
 * Groups contain a list messages of the same type by the same sender in a short span of time
 *
 * @param messages sorted array of conversation messages
 */
export function createMessageGroups(messages: Array<ConvoMessage>): Array<IMessageGroup> {
  const groups: Array<MessageGroup> = [];
  for (const msg of messages) {
    // get the last group
    const group = groups.at(-1);
    if (group?.belongs(msg)) {
      group.add(msg);
    } else {
      // Notice that this will be run if msg doesn't belong to the group
      // or if there's no group at all
      groups.push(new MessageGroup(msg));
    }
  }

  return groups.map((c) => c.toJSON());
}

/**
 * Grab a list of MessageGroups and insert a date separator when the day changes
 */
export function insertDateSeparators(
  msgGroups: Array<IMessageGroup>
): Array<IMessageGroup | IDateSeparator> {
  if (msgGroups.length === 0) {
    return msgGroups;
  }

  // group messages that happened in the same day
  // { '2018-01-02': [ ... ], '2018-01-03': [ ... ]}
  const byDate = groupBy(msgGroups, (chunk) => toDate(chunk.date));

  const list: Array<IMessageGroup | IDateSeparator> = [];

  for (const [date, messages] of Object.entries(byDate)) {
    list.push(newDateSeparator(moment.utc(date).toISOString()));
    list.push(...messages);
  }

  return list;
}

/**
 *
 * @param date ISO String
 */
function newDateSeparator(date: string): IDateSeparator {
  return {
    type: 'DateSeparator',
    date,
  };
}

/*
 * A group of messages that are
 *
 *  - the same type
 *  - the same sender
 *  - sent within 5 mins (MESSAGE_BUNCH_TIME_WINDOW)
 *
 */
class MessageGroup implements IMessageGroup {
  type: 'MessageGroup';
  date: string;
  messages: Array<ConvoMessage> = [];
  composer: ConvoMessageComposer;

  constructor(msg: ConvoMessage) {
    this.type = 'MessageGroup';
    this.date = msg.message.sentAt;
    this.composer = msg.message.composer;
    this.messages.push(msg);
  }

  toJSON(): IMessageGroup {
    return {
      type: this.type,
      date: this.date,
      messages: this.messages,
      composer: this.composer,
    };
  }

  add(msg: ConvoMessage) {
    invariant(this.messages.length > 0);
    invariant(this.isSameSender(msg), 'Messages in a chunk should be the same sender');
    invariant(
      this.isWithinDateRange(msg),
      'Messages in a chunk should be inside the given date range'
    );

    this.messages.push(msg);
  }

  isSameSender(msg: ConvoMessage): boolean {
    return this.messages[0].message.composer.uuid === msg.message.composer.uuid;
  }

  // TODO this actually doubles the date range, when we have more data we should
  // understand better the direction of the list and we can move things around to
  // set for 5 mins exactly
  isWithinDateRange(msg: ConvoMessage): boolean {
    return (
      Math.abs(new Date(this.date).getTime() - new Date(msg.message.sentAt).getTime()) <
      MESSAGE_BATCH_TIME_WINDOW
    );
  }

  /*
   * A chunk is a set of messages with:
   *
   *  - the same type
   *  - the same sender
   *  - sent within 5 mins (MESSAGE_BUNCH_TIME_WINDOW)
   *
   */
  belongs(msg: ConvoMessage): boolean {
    return this.isSameSender(msg) && this.isWithinDateRange(msg);
  }
}

/**
 *
 * This represents YYYY-MM-DD dates,
 * eventually it could be validated at the type level
 *
 */
export type YMDDate = string;

/**
 *
 * Turns an iso date into a YYYY-MM-DD date
 */
export function toDate(isoDate: string): YMDDate {
  return moment.utc(isoDate).format('YYYY-MM-DD');
}
